import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { captureUtm } from "@/lib/utm";
import { MetaManager } from "@/components/MetaManager";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00E676]"></div>
  </div>
);

// Components
import Header from "@/components/Header";
import ErrorBoundary from "@/components/ErrorBoundary";
import Footer from "@/components/Footer";
import LeadPopup from "@/components/LeadPopup";
import HubSpotTracking, { usePageTracking } from "@/components/HubSpotTracking";
import ScrollToTop from "@/components/ScrollToTop";
import SearchModal from "@/components/SearchModal";
import AIChatbot from "@/components/AIChatbot";
import BlogMockupPopup, { useBlogPopup, getPopupVariant } from "@/components/BlogMockupPopup";

// Pages
import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import IndustryPage from "@/pages/IndustryPage";
import BlogPage from "@/pages/BlogPage";
import BlogDetailPage from "@/pages/BlogDetailPage";
import BlogCategoryPage from "@/pages/BlogCategoryPage";
import ContactPage from "@/pages/ContactPage";
import AEOGEOPage from "@/pages/AEOGEOPage";
import CaseStudiesPage from "@/pages/CaseStudiesPage";
import CaseStudyDetailPage from "@/pages/CaseStudyDetailPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import QuotePaymentPage from "@/pages/QuotePaymentPage";
import QuoteSuccessPage from "@/pages/QuoteSuccessPage";
import ABDashboardPage from "@/pages/ABDashboardPage";
import EmailCampaignDashboard from "@/pages/EmailCampaignDashboard";
import EscalationDashboard from "@/pages/EscalationDashboard";
import GymMarketingPage from "@/pages/GymMarketingPage";
import VideoMarketingPage from "@/pages/VideoMarketingPage";
import AIReceptionistPage from "@/pages/AIReceptionistPage";
import ThankYouPage from "@/pages/ThankYouPage";
import ConversionThankYouPage from "@/pages/ConversionThankYouPage";
import NotFoundPage from "@/pages/NotFoundPage";
// New Industry Pages
import HVACMarketingPage from "@/pages/HVACMarketingPage";
import LandscapingMarketingPage from "@/pages/LandscapingMarketingPage";
import LawFirmMarketingPage from "@/pages/LawFirmMarketingPage";
import HealthcareMarketingPage from "@/pages/HealthcareMarketingPage";
import RealEstateMarketingPage from "@/pages/RealEstateMarketingPage";
import ConstructionMarketingPage from "@/pages/ConstructionMarketingPage";
// Legal Pages
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import BrandGuidelinesPage from "@/pages/BrandGuidelinesPage";
// Admin Pages
import GBPPostsPage from "@/pages/GBPPostsPage";
// AI Receptionist Industry Pages
import AIReceptionistLawFirmPage from "@/pages/AIReceptionistLawFirmPage";
import AIReceptionistDentalPage from "@/pages/AIReceptionistDentalPage";
import AIReceptionistHVACPage from "@/pages/AIReceptionistHVACPage";
import AIReceptionistPlumberPage from "@/pages/AIReceptionistPlumberPage";
import AIReceptionistGymPage from "@/pages/AIReceptionistGymPage";
import AIReceptionistRoofingPage from "@/pages/AIReceptionistRoofingPage";
import AIReceptionistAutoBodyPage from "@/pages/AIReceptionistAutoBodyPage";
import AIReceptionistSalonPage from "@/pages/AIReceptionistSalonPage";
// AI Receptionist Demo - Lazy loaded
const AIReceptionistDemoPage = lazy(() => import("@/pages/AIReceptionistDemoPage"));
const ChecklistsHubPage = lazy(() => import("@/pages/ChecklistsHubPage"));
const ChecklistLandingPage = lazy(() => import("@/pages/ChecklistLandingPage"));
const UTMDashboardPage = lazy(() => import("@/pages/UTMDashboardPage"));
const AboutDanielPage = lazy(() => import("@/pages/AboutDanielPage"));
const EditorialPolicyPage = lazy(() => import("@/pages/EditorialPolicyPage"));
const InsightsPage = lazy(() => import("@/pages/InsightsPage"));
const GymMarketingLP = lazy(() => import("@/pages/lp/GymMarketingLP"));
const GymMarketingThanks = lazy(() => import("@/pages/lp/GymMarketingThanks"));
const GymSeoPage = lazy(() => import("@/pages/GymSeoPage"));
const GymWebsiteDesignPage = lazy(() => import("@/pages/GymWebsiteDesignPage"));

// Service Pages - Lazy loaded to prevent Babel stack overflow
const SEOServicesPage = lazy(() => import("@/pages/services/SEOServicesPage"));
const LocalSEOPage = lazy(() => import("@/pages/services/LocalSEOPage"));
const PPCServicesPage = lazy(() => import("@/pages/services/PPCServicesPage"));
const WebDesignPage = lazy(() => import("@/pages/services/WebDesignPage"));
const GBPOptimizationPage = lazy(() => import("@/pages/services/GBPOptimizationPage"));
const ContentMarketingPage = lazy(() => import("@/pages/services/ContentMarketingPage"));
const SocialMediaPage = lazy(() => import("@/pages/services/SocialMediaPage"));
const ReputationManagementPage = lazy(() => import("@/pages/services/ReputationManagementPage"));
const EmailSMSMarketingPage = lazy(() => import("@/pages/services/EmailSMSMarketingPage"));
const MulticulturalMarketingPage = lazy(() => import("@/pages/services/MulticulturalMarketingPage"));
const GEOServicePage = lazy(() => import("@/pages/services/GEOServicePage"));
const AEOServicePage = lazy(() => import("@/pages/services/AEOServicePage"));
const LLMOptimizationPage = lazy(() => import("@/pages/services/LLMOptimizationPage"));

// Lead Magnet Pages
import FreeAIVisibilityAuditPage from "@/pages/FreeAIVisibilityAuditPage";

// Definition Pages
import WhatIsAEOPage from "@/pages/WhatIsAEOPage";
import WhatIsGEOPage from "@/pages/WhatIsGEOPage";
import WhatIsLLMOPage from "@/pages/WhatIsLLMOPage";
import AEOvsGEOvsSEOPage from "@/pages/AEOvsGEOvsSEOPage";

// City Landing Pages
import CityServicePage from "@/pages/CityServicePage";

// Knowledge Hub
import LearnPage from "@/pages/LearnPage";

// Industry Pages - Lazy loaded
const DentalSEOPage = lazy(() => import("@/pages/industries/DentalSEOPage"));
const PlumberSEOPage = lazy(() => import("@/pages/industries/PlumberSEOPage"));
const RooferSEOPage = lazy(() => import("@/pages/industries/RooferSEOPage"));
const MedSpaSEOPage = lazy(() => import("@/pages/industries/MedSpaSEOPage"));
const EcommerceSEOPage = lazy(() => import("@/pages/industries/EcommerceSEOPage"));
const MovingCompanySEOPage = lazy(() => import("@/pages/industries/MovingCompanySEOPage"));
const ContractorSEOPage = lazy(() => import("@/pages/industries/ContractorSEOPage"));
const SalonSEOPage = lazy(() => import("@/pages/industries/SalonSEOPage"));
const AutoShopSEOPage = lazy(() => import("@/pages/industries/AutoShopSEOPage"));
const SaaSMarketingPage = lazy(() => import("@/pages/industries/SaaSMarketingPage"));
const SportsPerformancePage = lazy(() => import("@/pages/industries/SportsPerformancePage"));

// Service Pages - Additional
const AIAutomationPage = lazy(() => import("@/pages/services/AIAutomationPage"));
const SmartSitePage = lazy(() => import("@/pages/services/SmartSitePage"));

// AI Operations Service Pages
const AIOperationsPage = lazy(() => import("@/pages/services/AIOperationsPage"));
const AILeadAgentPage = lazy(() => import("@/pages/services/AILeadAgentPage"));
const AIContentEnginePage = lazy(() => import("@/pages/services/AIContentEnginePage"));
const AIInfluencerOutreachPage = lazy(() => import("@/pages/services/AIInfluencerOutreachPage"));
const AICoachingAgentPage = lazy(() => import("@/pages/services/AICoachingAgentPage"));
const AIPostCallOpsPage = lazy(() => import("@/pages/services/AIPostCallOpsPage"));
const AIKnowledgeAgentPage = lazy(() => import("@/pages/services/AIKnowledgeAgentPage"));

// Hub Pages - Industry Groupings
const HomeServicesHubPage = lazy(() => import("@/pages/hubs/HomeServicesHubPage"));
const HealthcareHubPage = lazy(() => import("@/pages/hubs/HealthcareHubPage"));
const ProfessionalServicesHubPage = lazy(() => import("@/pages/hubs/ProfessionalServicesHubPage"));
const AutomotiveHubPage = lazy(() => import("@/pages/hubs/AutomotiveHubPage"));
const EcommerceHubPage = lazy(() => import("@/pages/hubs/EcommerceHubPage"));
const MovingCompanyHubPage = lazy(() => import("@/pages/hubs/MovingCompanyHubPage"));

// About Page
const AboutPage = lazy(() => import("@/pages/AboutPage"));

// HYROX Content Page
const HyroxContentPage = lazy(() => import("@/pages/HyroxContentPage"));

// AI Operator Setup Page
const AIOperatorSetupPage = lazy(() => import("@/pages/AIOperatorSetupPage"));

// ClawdBot Setup Guide
const ClawdBotSetupGuide = lazy(() => import("@/pages/guides/ClawdBotSetupGuide"));

// Contractor Marketing Strategies 2026 Guide
const ContractorMarketingStrategies2026 = lazy(() => import("@/pages/guides/ContractorMarketingStrategies2026"));

// ClawdBot Use Cases Blog
const ClawdBotUseCases = lazy(() => import("@/pages/guides/ClawdBotUseCases"));

// Local SEO Ranking Factors Guide
const LocalSEORankingFactorsGuide = lazy(() => import("@/pages/guides/LocalSEORankingFactorsGuide"));

// Interactive Guide Pages
const GuidesHub = lazy(() => import("@/pages/GuidesHub"));
const GymMarketingGuide = lazy(() => import("@/pages/guides/GymMarketingGuide"));
const HVACMarketingGuide = lazy(() => import("@/pages/guides/HVACMarketingGuide"));
const LawFirmMarketingGuide = lazy(() => import("@/pages/guides/LawFirmMarketingGuide"));
const HealthcareMarketingGuide = lazy(() => import("@/pages/guides/HealthcareMarketingGuide"));
const RealEstateMarketingGuide = lazy(() => import("@/pages/guides/RealEstateMarketingGuide"));
const LandscapingMarketingGuide = lazy(() => import("@/pages/guides/LandscapingMarketingGuide"));
const ConstructionMarketingGuide = lazy(() => import("@/pages/guides/ConstructionMarketingGuide"));

// Tools Pages
const PPCCalculatorPage = lazy(() => import("@/pages/tools/PPCCalculatorPage"));

// Special Article Pages
const YouTubeSEOGuide = lazy(() => import("@/pages/blog/YouTubeSEOGuide"));
const InstagramLikesGuide = lazy(() => import("@/pages/blog/InstagramLikesGuide"));

// Canva Pricing Interactive Guide
const CanvaPricingGuide = lazy(() => import("@/pages/blog/CanvaPricingGuide"));

// B2B PPC Strategy Guide
const B2BPPCStrategyGuide = lazy(() => import("@/pages/blog/B2BPPCStrategyGuide"));

// iOS App Marketing Videos Guide
const IOSAppMarketingVideosGuide = lazy(() => import("@/pages/blog/IOSAppMarketingVideosGuide"));

// Best AI Tools Guide
const BestAIToolsGuide = lazy(() => import("@/pages/blog/BestAIToolsGuide"));
const RumbleMoneyGuide = lazy(() => import("@/pages/blog/RumbleMoneyGuide"));
const QRCodeGuide = lazy(() => import("@/pages/blog/QRCodeGuide"));
const SocialMediaMarketingGuide = lazy(() => import("@/pages/blog/SocialMediaMarketingGuide"));
const EmailMarketingGuide = lazy(() => import("@/pages/blog/EmailMarketingGuide"));
const SEOChecklistGuide = lazy(() => import("@/pages/blog/SEOChecklistGuide"));
const GoogleAdsGuide = lazy(() => import("@/pages/blog/GoogleAdsGuide"));
const SocialMediaMoneyGuide = lazy(() => import("@/pages/blog/SocialMediaMoneyGuide"));
const TikTokFamousGuide = lazy(() => import("@/pages/blog/TikTokFamousGuide"));
const YouTubeViewsGuide = lazy(() => import("@/pages/blog/YouTubeViewsGuide"));
const FacebookAdsGuide = lazy(() => import("@/pages/blog/FacebookAdsGuide"));
const LinkedInMarketingGuide = lazy(() => import("@/pages/blog/LinkedInMarketingGuide"));
const InstagramMarketingGuide = lazy(() => import("@/pages/blog/InstagramMarketingGuide"));
const WebsiteTrafficGuide = lazy(() => import("@/pages/blog/WebsiteTrafficGuide"));
const RankHigherGuide = lazy(() => import("@/pages/blog/RankHigherGuide"));
const LinkBuildingGuide = lazy(() => import("@/pages/blog/LinkBuildingGuide"));
const KeywordResearchGuide = lazy(() => import("@/pages/blog/KeywordResearchGuide"));
const CROGuide = lazy(() => import("@/pages/blog/CROGuide"));
const ContentMarketingGuide = lazy(() => import("@/pages/blog/ContentMarketingGuide"));
const GrowthHackingGuide = lazy(() => import("@/pages/blog/GrowthHackingGuide"));
const SEOGuide = lazy(() => import("@/pages/blog/SEOGuide"));
const StartBlogGuide = lazy(() => import("@/pages/blog/StartBlogGuide"));
const ShopifySEOGuide = lazy(() => import("@/pages/blog/ShopifySEOGuide"));
const VideoMarketingGuide = lazy(() => import("@/pages/blog/VideoMarketingGuide"));
const AIForSEOGuide = lazy(() => import("@/pages/blog/AIForSEOGuide"));
const GBPGuide = lazy(() => import("@/pages/blog/GBPGuide"));
const LandingPageGuide = lazy(() => import("@/pages/blog/LandingPageGuide"));
const MakeMoneyOnlineGuide = lazy(() => import("@/pages/blog/MakeMoneyOnlineGuide"));
const PinterestMarketingGuide = lazy(() => import("@/pages/blog/PinterestMarketingGuide"));
const TikTokAffiliateGuide = lazy(() => import("@/pages/blog/TikTokAffiliateGuide"));
const VoiceSearchGuide = lazy(() => import("@/pages/blog/VoiceSearchGuide"));
const WebDesignGuide = lazy(() => import("@/pages/blog/WebDesignGuide"));

// Special Article Pages
const EmergentArticlePage = lazy(() => import("@/pages/blog/EmergentArticlePage"));

// PPC Landing Pages
const LocalSEOLanding = lazy(() => import("@/pages/lp/LocalSEOLanding"));
const GBPLanding = lazy(() => import("@/pages/lp/GBPLanding"));
const VideoMarketingLanding = lazy(() => import("@/pages/lp/VideoMarketingLanding"));
const AEOGEOLanding = lazy(() => import("@/pages/lp/AEOGEOLanding"));

// Admin Pages
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminServices = lazy(() => import("@/pages/admin/AdminServices"));
const AdminBlog = lazy(() => import("@/pages/admin/AdminBlog"));
const AdminCaseStudies = lazy(() => import("@/pages/admin/AdminCaseStudies"));
const AdminFAQs = lazy(() => import("@/pages/admin/AdminFAQs"));
const AdminGBPPosts = lazy(() => import("@/pages/admin/AdminGBPPosts"));
const AdminAnalytics = lazy(() => import("@/pages/admin/AdminAnalytics"));
const AdminQuotes = lazy(() => import("@/pages/admin/AdminQuotes"));
const AdminLeads = lazy(() => import("@/pages/admin/AdminLeads"));

// Page tracking wrapper component
const PageTracker = ({ children }) => {
  usePageTracking();
  return children;
};

// Main App Layout
const AppLayout = () => {
  const [popupState, setPopupState] = useState({ isOpen: false, type: 'ebook' });
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { captureUtm(); }, []);

  // One contextual auto-popup max: relevant to the page, shown once ever per visitor
  const popupVariant = getPopupVariant(location.pathname);
  const [blogPopupOpen, setBlogPopupOpen] = useBlogPopup({
    delay: 45000,
    scrollThreshold: 60,
    variant: popupVariant,
  });

  const openPopup = (type) => setPopupState({ isOpen: true, type });
  const closePopup = () => setPopupState({ ...popupState, isOpen: false });

  return (
    <PageTracker>
      <ScrollToTop />
      <MetaManager />
      <div className="min-h-screen bg-black text-white">
        <Header onOpenPopup={openPopup} onOpenSearch={() => setSearchOpen(true)} />
        
        <main>
          <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage onOpenPopup={openPopup} />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/:slug" element={<ServiceDetailPage />} />
            <Route path="/industries/:slug" element={<IndustryPage />} />
            <Route path="/gym-marketing" element={<GymMarketingPage />} />
            <Route path="/gym-seo" element={<GymSeoPage />} />
            <Route path="/gym-website-design" element={<GymWebsiteDesignPage />} />
            <Route path="/video-marketing" element={<VideoMarketingPage />} />
            <Route path="/ai-receptionist" element={<AIReceptionistPage />} />
            <Route path="/ai-receptionist-demo" element={<AIReceptionistDemoPage />} />
            <Route path="/ai-receptionist-law-firms" element={<AIReceptionistLawFirmPage />} />
            <Route path="/ai-receptionist-dentists" element={<AIReceptionistDentalPage />} />
            <Route path="/ai-receptionist-hvac" element={<AIReceptionistHVACPage />} />
            <Route path="/ai-receptionist-plumbers" element={<AIReceptionistPlumberPage />} />
            <Route path="/ai-receptionist-gyms" element={<AIReceptionistGymPage />} />
            <Route path="/ai-receptionist-roofers" element={<AIReceptionistRoofingPage />} />
            <Route path="/ai-receptionist-auto-body" element={<AIReceptionistAutoBodyPage />} />
            <Route path="/ai-receptionist-salons" element={<AIReceptionistSalonPage />} />
            <Route path="/hyrox-content" element={<HyroxContentPage />} />
            <Route path="/ai-operator-setup" element={<AIOperatorSetupPage />} />
            
            {/* ClawdBot Setup Guide */}
            <Route path="/guides/clawdbot-setup" element={<ClawdBotSetupGuide />} />
            
            {/* Local SEO Ranking Factors Guide */}
            <Route path="/guides/local-seo-ranking-factors" element={<LocalSEORankingFactorsGuide />} />
            
            {/* Contractor Marketing Strategies 2026 Guide */}
            <Route path="/blog/contractor-marketing-strategies-2026" element={<ContractorMarketingStrategies2026 />} />
            
            {/* ClawdBot Use Cases Blog */}
            <Route path="/blog/clawdbot-use-cases" element={<ClawdBotUseCases />} />
            
            {/* Interactive Guide Pages */}
            <Route path="/guides" element={<GuidesHub />} />
            <Route path="/guides/gym-marketing" element={<GymMarketingGuide />} />
            <Route path="/guides/hvac-marketing" element={<HVACMarketingGuide />} />
            <Route path="/guides/law-firm-marketing" element={<LawFirmMarketingGuide />} />
            <Route path="/guides/healthcare-marketing" element={<HealthcareMarketingGuide />} />
            <Route path="/guides/real-estate-marketing" element={<RealEstateMarketingGuide />} />
            <Route path="/guides/landscaping-marketing" element={<LandscapingMarketingGuide />} />
            <Route path="/guides/construction-marketing" element={<ConstructionMarketingGuide />} />
            
            {/* Tools Pages */}
            <Route path="/tools/ppc-calculator" element={<PPCCalculatorPage />} />
            
            {/* Special Article Pages */}
            <Route path="/blog/emergent-ai-app-builder-build-app-for-free" element={<EmergentArticlePage />} />
            <Route path="/blog/youtube-seo" element={<YouTubeSEOGuide />} />
            <Route path="/blog/how-to-see-what-photos-someone-likes-on-instagram" element={<InstagramLikesGuide />} />
            <Route path="/blog/canva-free-vs-pro-vs-teams-pricing" element={<CanvaPricingGuide />} />
            <Route path="/blog/b2b-ppc-strategy" element={<B2BPPCStrategyGuide />} />
            <Route path="/blog/creating-ios-app-marketing-videos" element={<IOSAppMarketingVideosGuide />} />
            <Route path="/blog/best-ai-tools" element={<BestAIToolsGuide />} />
            <Route path="/blog/how-to-make-money-on-rumble" element={<RumbleMoneyGuide />} />
            <Route path="/blog/static-vs-dynamic-qr-codes" element={<QRCodeGuide />} />
            <Route path="/blog/social-media-marketing-guide" element={<SocialMediaMarketingGuide />} />
            <Route path="/blog/email-marketing-guide" element={<EmailMarketingGuide />} />
            <Route path="/blog/seo-checklist" element={<SEOChecklistGuide />} />
            <Route path="/blog/google-ads-guide" element={<GoogleAdsGuide />} />
            <Route path="/blog/how-to-make-money-on-social-media" element={<SocialMediaMoneyGuide />} />
            <Route path="/blog/how-to-become-tiktok-famous" element={<TikTokFamousGuide />} />
            <Route path="/blog/how-to-get-more-views-on-youtube" element={<YouTubeViewsGuide />} />
            <Route path="/blog/facebook-advertising-guide" element={<FacebookAdsGuide />} />
            <Route path="/blog/linkedin-marketing-strategy" element={<LinkedInMarketingGuide />} />
            <Route path="/blog/instagram-marketing-strategy" element={<InstagramMarketingGuide />} />
            <Route path="/blog/how-to-increase-website-traffic" element={<WebsiteTrafficGuide />} />
            <Route path="/blog/how-to-rank-higher-on-google" element={<RankHigherGuide />} />
            <Route path="/blog/link-building-guide" element={<LinkBuildingGuide />} />
            <Route path="/blog/keyword-research-guide" element={<KeywordResearchGuide />} />
            <Route path="/blog/conversion-rate-optimization-guide" element={<CROGuide />} />
            <Route path="/blog/content-marketing-strategy-guide" element={<ContentMarketingGuide />} />
            <Route path="/blog/growth-hacking-guide" element={<GrowthHackingGuide />} />
            <Route path="/blog/seo-complete-guide" element={<SEOGuide />} />
            <Route path="/blog/how-to-start-a-blog-guide" element={<StartBlogGuide />} />
            <Route path="/blog/shopify-seo-guide" element={<ShopifySEOGuide />} />
            <Route path="/blog/video-marketing-guide" element={<VideoMarketingGuide />} />
            <Route path="/blog/ai-for-seo-guide" element={<AIForSEOGuide />} />
            <Route path="/blog/google-business-profile-guide" element={<GBPGuide />} />
            <Route path="/blog/landing-page-optimization-guide" element={<LandingPageGuide />} />
            <Route path="/blog/make-money-online-guide" element={<MakeMoneyOnlineGuide />} />
            <Route path="/blog/pinterest-marketing-guide" element={<PinterestMarketingGuide />} />
            <Route path="/blog/tiktok-affiliate-marketing-guide" element={<TikTokAffiliateGuide />} />
            <Route path="/blog/voice-search-optimization-guide" element={<VoiceSearchGuide />} />
            <Route path="/blog/website-design-guide" element={<WebDesignGuide />} />

            {/* PPC Landing Pages */}
            <Route path="/lp/local-seo" element={<LocalSEOLanding />} />
            <Route path="/lp/gbp-optimization" element={<GBPLanding />} />
            <Route path="/lp/video-marketing" element={<VideoMarketingLanding />} />
            <Route path="/lp/aeo-geo" element={<AEOGEOLanding />} />

            {/* Service Pages - Lazy Loaded */}
            <Route path="/services/seo" element={<SEOServicesPage />} />
            <Route path="/services/local-seo" element={<LocalSEOPage />} />
            <Route path="/services/ppc" element={<PPCServicesPage />} />
            <Route path="/services/web-design" element={<WebDesignPage />} />
            <Route path="/services/gbp-optimization" element={<GBPOptimizationPage />} />
            <Route path="/services/content-marketing" element={<ContentMarketingPage />} />
            <Route path="/services/social-media" element={<SocialMediaPage />} />
            <Route path="/services/reputation-management" element={<ReputationManagementPage />} />
            <Route path="/services/email-sms-marketing" element={<EmailSMSMarketingPage />} />
            <Route path="/services/multicultural-marketing" element={<MulticulturalMarketingPage />} />
            <Route path="/services/ai-automation" element={<AIAutomationPage />} />
            <Route path="/services/ai-operations" element={<AIOperationsPage />} />
            <Route path="/services/ai-lead-agent" element={<AILeadAgentPage />} />
            <Route path="/services/ai-content-engine" element={<AIContentEnginePage />} />
            <Route path="/services/ai-influencer-outreach" element={<AIInfluencerOutreachPage />} />
            <Route path="/services/ai-coaching-agent" element={<AICoachingAgentPage />} />
            <Route path="/services/ai-post-call-ops" element={<AIPostCallOpsPage />} />
            <Route path="/services/ai-knowledge-agent" element={<AIKnowledgeAgentPage />} />
            <Route path="/smart-site" element={<SmartSitePage />} />
            <Route path="/services/smart-site" element={<SmartSitePage />} />
            {/* Industry Pages - Lazy Loaded */}
            <Route path="/seo-for-dentists" element={<DentalSEOPage />} />
            <Route path="/seo-for-plumbers" element={<PlumberSEOPage />} />
            <Route path="/seo-for-roofers" element={<RooferSEOPage />} />
            <Route path="/med-spa-marketing" element={<MedSpaSEOPage />} />
            <Route path="/ecommerce-seo" element={<EcommerceSEOPage />} />
            <Route path="/moving-company-marketing" element={<MovingCompanySEOPage />} />
            <Route path="/contractor-marketing" element={<ContractorSEOPage />} />
            <Route path="/salon-marketing" element={<SalonSEOPage />} />
            <Route path="/auto-shop-marketing" element={<AutoShopSEOPage />} />
            <Route path="/sports-performance-marketing" element={<SportsPerformancePage />} />
            <Route path="/saas-marketing" element={<SaaSMarketingPage />} />
            {/* Industry Hub Pages */}
            <Route path="/home-services-marketing" element={<HomeServicesHubPage />} />
            <Route path="/healthcare-wellness-marketing" element={<HealthcareHubPage />} />
            <Route path="/professional-services-marketing" element={<ProfessionalServicesHubPage />} />
            <Route path="/automotive-marketing" element={<AutomotiveHubPage />} />
            <Route path="/ecommerce-marketing" element={<EcommerceHubPage />} />
            <Route path="/moving-company-hub" element={<MovingCompanyHubPage />} />
            {/* About Page */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/about/daniel-rivas" element={<AboutDanielPage />} />
            {/* Existing Industry Pages */}
            <Route path="/hvac-marketing" element={<HVACMarketingPage />} />
            <Route path="/landscaping-marketing" element={<LandscapingMarketingPage />} />
            <Route path="/law-firm-marketing" element={<LawFirmMarketingPage />} />
            <Route path="/healthcare-marketing" element={<HealthcareMarketingPage />} />
            <Route path="/real-estate-marketing" element={<RealEstateMarketingPage />} />
            <Route path="/construction-marketing" element={<ConstructionMarketingPage />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/thank-you/lead" element={<ConversionThankYouPage variant="lead" />} />
            <Route path="/thank-you/swipe-file" element={<ConversionThankYouPage variant="swipe-file" />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/category/:category" element={<BlogCategoryPage />} />
            <Route path="/blog/:slug" element={<BlogDetailPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/aeo-geo" element={<AEOGEOPage />} />
            <Route path="/case-studies" element={<CaseStudiesPage />} />
            <Route path="/case-studies/:slug" element={<CaseStudyDetailPage />} />
            <Route path="/checklists" element={<ChecklistsHubPage />} />
            <Route path="/checklists/:slug" element={<ChecklistLandingPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/quote/pay/:token" element={<QuotePaymentPage />} />
            <Route path="/quote/success" element={<QuoteSuccessPage />} />
            <Route path="/admin/ab-dashboard" element={<ABDashboardPage />} />
            <Route path="/admin/email-campaigns" element={<EmailCampaignDashboard />} />
            <Route path="/admin/escalations" element={<EscalationDashboard />} />
            <Route path="/admin/gbp-posts" element={<GBPPostsPage />} />
            <Route path="/admin/utm-dashboard" element={<UTMDashboardPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/editorial-policy" element={<EditorialPolicyPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/insights/*" element={<InsightsPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/brand-guidelines" element={<BrandGuidelinesPage />} />
            
            {/* New Service Pages */}
            <Route path="/services/geo" element={<GEOServicePage />} />
            <Route path="/services/aeo" element={<AEOServicePage />} />
            <Route path="/services/llm-optimization" element={<LLMOptimizationPage />} />
            
            {/* Lead Magnet Pages */}
            <Route path="/free-ai-visibility-audit" element={<FreeAIVisibilityAuditPage />} />
            
            {/* Knowledge Hub */}
            <Route path="/learn" element={<LearnPage />} />
            
            {/* Definition / Pillar Pages */}
            <Route path="/what-is-aeo" element={<WhatIsAEOPage />} />
            <Route path="/what-is-geo" element={<WhatIsGEOPage />} />
            <Route path="/what-is-llmo" element={<WhatIsLLMOPage />} />
            <Route path="/aeo-vs-geo-vs-seo" element={<AEOvsGEOvsSEOPage />} />
            
            {/* Texas City Landing Pages — SEO */}
            <Route path="/seo-el-paso" element={<CityServicePage citySlug="el-paso" serviceSlug="seo" />} />
            <Route path="/seo-lubbock" element={<CityServicePage citySlug="lubbock" serviceSlug="seo" />} />
            <Route path="/seo-waco" element={<CityServicePage citySlug="waco" serviceSlug="seo" />} />
            <Route path="/seo-killeen" element={<CityServicePage citySlug="killeen" serviceSlug="seo" />} />
            <Route path="/seo-amarillo" element={<CityServicePage citySlug="amarillo" serviceSlug="seo" />} />
            {/* Texas City Landing Pages — Local SEO */}
            <Route path="/local-seo-el-paso" element={<CityServicePage citySlug="el-paso" serviceSlug="local-seo" />} />
            <Route path="/local-seo-lubbock" element={<CityServicePage citySlug="lubbock" serviceSlug="local-seo" />} />
            <Route path="/local-seo-waco" element={<CityServicePage citySlug="waco" serviceSlug="local-seo" />} />
            <Route path="/local-seo-killeen" element={<CityServicePage citySlug="killeen" serviceSlug="local-seo" />} />
            <Route path="/local-seo-amarillo" element={<CityServicePage citySlug="amarillo" serviceSlug="local-seo" />} />
            {/* Texas City Landing Pages — GBP */}
            <Route path="/gbp-optimization-el-paso" element={<CityServicePage citySlug="el-paso" serviceSlug="gbp-optimization" />} />
            <Route path="/gbp-optimization-lubbock" element={<CityServicePage citySlug="lubbock" serviceSlug="gbp-optimization" />} />
            <Route path="/gbp-optimization-waco" element={<CityServicePage citySlug="waco" serviceSlug="gbp-optimization" />} />
            <Route path="/gbp-optimization-killeen" element={<CityServicePage citySlug="killeen" serviceSlug="gbp-optimization" />} />
            <Route path="/gbp-optimization-amarillo" element={<CityServicePage citySlug="amarillo" serviceSlug="gbp-optimization" />} />
            {/* Catch-all: real 404 with noindex */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
        </main>

        <Footer />
        <LeadPopup isOpen={popupState.isOpen} onClose={closePopup} type={popupState.type} />
        <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
        <BlogMockupPopup isOpen={blogPopupOpen} onClose={() => setBlogPopupOpen(false)} variant={popupVariant} />
        <AIChatbot />
        <HubSpotTracking />
        <Toaster position="top-right" richColors />
      </div>
    </PageTracker>
  );
};

// Placeholder for pages not yet implemented
const PlaceholderPage = ({ title }) => (
  <div className="min-h-screen pt-32 pb-20" data-testid="placeholder-page">
    <div className="container-custom text-center">
      <h1 className="text-4xl md:text-5xl font-bold mb-6">{title}</h1>
      <p className="text-gray-400 text-lg">This page is coming soon.</p>
    </div>
  </div>
);

// GA4 SPA page_view on client-side route changes (initial load handled by gtag config)
const GA4RouteTracker = () => {
  const location = useLocation();
  const lastTracked = useRef(window.location.pathname + window.location.search);
  useEffect(() => {
    const current = location.pathname + location.search;
    if (lastTracked.current === current) return;
    lastTracked.current = current;
    const t = setTimeout(() => {
      if (typeof window.gtag === "function") {
        window.gtag("event", "page_view", {
          page_location: window.location.href,
          page_title: document.title,
        });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [location.pathname, location.search]);
  return null;
};

// App wrapper with Router
function App() {
  return (
    <BrowserRouter>
      <GA4RouteTracker />
      <Suspense fallback={<div className="min-h-screen bg-black flex items-center justify-center"><div className="text-white">Loading...</div></div>}>
        <Routes>
          {/* Admin Routes - Outside of AppLayout */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />}>
            <Route path="services" element={<AdminServices />} />
            <Route path="case-studies" element={<AdminCaseStudies />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="faqs" element={<AdminFAQs />} />
            <Route path="gbp-posts" element={<AdminGBPPosts />} />
            <Route path="quotes" element={<AdminQuotes />} />
            <Route path="leads" element={<AdminLeads />} />
          </Route>
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          {/* Ads Landing Pages - Outside of AppLayout (no global nav/footer) */}
          <Route path="/lp/gym-marketing" element={<GymMarketingLP />} />
          <Route path="/lp/gym-marketing/thanks" element={<GymMarketingThanks />} />
          {/* Main App Routes */}
          <Route path="/*" element={<AppLayout />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
